import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Crown, Loader2, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import CompactSelectPicker from './ui/CompactSelectPicker';
import teamVaultService, { teamVaultErrorMessage } from '../services/teamVaultService';
import { useTeamVaultStore, selectTeamEntry, loadTeamVault } from '../store/teamVaultStore';
import {
  SCOPE_ROOT,
  buildGrantPayload,
  canAdministerGrants,
  grantScopeOptions,
  grantScopeValue,
  grantsForMember,
  hasImplicitVaultAccess,
} from '../utils/teamVaultGrants';
import './TeamVaultGrants.css';

/**
 * Team vault access, per member — the grant admin surface (owner/admin only).
 *
 * The whole panel is gated on the SERVER's `can_administer`, not on a role
 * string this component computed. It renders nothing at all otherwise: an empty
 * tab is a better answer than a tab full of controls every one of whose actions
 * the server will refuse.
 *
 * The decisions live in `utils/teamVaultGrants.js` so they are testable without
 * mounting this; what is left here is the fetch, the list and the two pickers.
 */
export default function TeamVaultGrants({ teamId, members = [] }) {
  const { t } = useTranslation();
  const byTeam = useTeamVaultStore((s) => s.byTeam);
  const entry = useMemo(() => selectTeamEntry(byTeam, teamId), [byTeam, teamId]);

  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  // memberId -> { scope, permission } for the "add" row, so two members' draft
  // grants never share one piece of state.
  const [drafts, setDrafts] = useState({});

  const isAdmin = canAdministerGrants(entry);

  useEffect(() => {
    if (teamId != null && !entry.loaded) loadTeamVault(teamId);
  }, [teamId, entry.loaded]);

  const loadGrants = useCallback(async () => {
    if (teamId == null || !isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      setGrants(await teamVaultService.listGrants(teamId));
    } catch (e) {
      setGrants([]);
      setError(teamVaultErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [teamId, isAdmin]);

  useEffect(() => {
    loadGrants();
  }, [loadGrants]);

  const scopeOptions = useMemo(() => grantScopeOptions(entry.folders), [entry.folders]);

  const scopeLabel = useCallback(
    (option) => {
      if (!option) return '';
      if (option.kind === 'vault') return t('teamVault.scopeVault');
      if (option.kind === 'root') return t('teamVault.scopeRoot');
      return option.folderName;
    },
    [t]
  );

  const labelForScopeValue = useCallback(
    (value) => scopeLabel(scopeOptions.find((o) => o.value === value)) || t('teamVault.scopeGone'),
    [scopeOptions, scopeLabel, t]
  );

  const draftFor = (memberId) => drafts[memberId] || { scope: SCOPE_ROOT, permission: 'read' };
  const setDraft = (memberId, patch) =>
    setDrafts((d) => ({ ...d, [memberId]: { ...draftFor(memberId), ...patch } }));

  const addGrant = async (member) => {
    const draft = draftFor(member.id);
    const busy = `add:${member.id}`;
    setBusyKey(busy);
    setError(null);
    try {
      await teamVaultService.putGrant(
        teamId,
        buildGrantPayload(member.id, draft.scope, draft.permission)
      );
      await loadGrants();
      setDrafts((d) => ({ ...d, [member.id]: { scope: SCOPE_ROOT, permission: 'read' } }));
    } catch (e) {
      setError(teamVaultErrorMessage(e));
    } finally {
      setBusyKey(null);
    }
  };

  const revokeGrant = async (grant) => {
    setBusyKey(`del:${grant.id}`);
    setError(null);
    try {
      await teamVaultService.deleteGrant(teamId, grant.id);
      await loadGrants();
    } catch (e) {
      setError(teamVaultErrorMessage(e));
    } finally {
      setBusyKey(null);
    }
  };

  if (!isAdmin) return null;

  const permissionOptions = [
    { value: 'read', label: t('teamVault.permissionRead') },
    { value: 'write', label: t('teamVault.permissionWrite') },
  ];

  return (
    <div className="team-vault-grants">
      <div className="team-vault-grants-head">
        <h3 className="team-vault-grants-title">{t('teamVault.title')}</h3>
        {/* Plain statement of the trade this design makes. NOT an "end-to-end"
            claim and never to become one: the team vault is Cloak-encrypted at
            rest so the server can read it, which is precisely what makes
            revoking a grant real rather than cosmetic. */}
        <p className="team-vault-grants-sub">{t('teamVault.subtitle')}</p>
      </div>

      {error && <div className="team-vault-grants-error">{error}</div>}

      {loading ? (
        <div className="team-vault-grants-loading">
          <Loader2 size={14} className="animate-spin" aria-hidden />
          <span>{t('teamVault.loading')}</span>
        </div>
      ) : (
        <div className="team-vault-grants-list">
          {members.map((member) => {
            const mine = grantsForMember(grants, member.id);
            const implicit = hasImplicitVaultAccess(member);
            const draft = draftFor(member.id);
            return (
              <div key={member.id} className="team-vault-grant-row">
                <div className="team-vault-grant-who">
                  <span className="team-vault-grant-name">{member.name || member.email}</span>
                  <span className="team-vault-grant-role">{member.role}</span>
                </div>

                {implicit ? (
                  // Owners and admins cannot be reduced by a grant, so showing
                  // them an empty list (or an Add control that does nothing
                  // useful) would misdescribe their access.
                  <div className="team-vault-grant-implicit">
                    <Crown size={12} aria-hidden />
                    <span>{t('teamVault.implicitAccess')}</span>
                  </div>
                ) : (
                  <>
                    <div className="team-vault-grant-current">
                      {mine.length === 0 ? (
                        <span className="team-vault-grant-none">{t('teamVault.noAccess')}</span>
                      ) : (
                        mine.map((grant) => (
                          <span key={grant.id} className="team-vault-grant-chip">
                            <ShieldCheck size={11} aria-hidden />
                            {labelForScopeValue(grantScopeValue(grant))}
                            <em>
                              {grant.permission === 'write'
                                ? t('teamVault.permissionWrite')
                                : t('teamVault.permissionRead')}
                            </em>
                            <button
                              type="button"
                              className="team-vault-grant-revoke"
                              disabled={busyKey === `del:${grant.id}`}
                              title={t('teamVault.revoke')}
                              aria-label={t('teamVault.revoke')}
                              onClick={() => revokeGrant(grant)}
                            >
                              <Trash2 size={11} aria-hidden />
                            </button>
                          </span>
                        ))
                      )}
                    </div>

                    <div className="team-vault-grant-add">
                      <CompactSelectPicker
                        className="team-vault-grant-picker"
                        value={draft.scope}
                        onChange={(scope) => setDraft(member.id, { scope })}
                        options={scopeOptions.map((o) => ({
                          value: o.value,
                          label: scopeLabel(o),
                        }))}
                        aria-label={t('teamVault.scopeLabel')}
                      />
                      <CompactSelectPicker
                        className="team-vault-grant-picker"
                        value={draft.permission}
                        onChange={(permission) => setDraft(member.id, { permission })}
                        options={permissionOptions}
                        aria-label={t('teamVault.permissionLabel')}
                      />
                      <button
                        type="button"
                        className="team-vault-grant-add-btn"
                        disabled={busyKey === `add:${member.id}`}
                        onClick={() => addGrant(member)}
                      >
                        <Plus size={12} aria-hidden /> {t('teamVault.grant')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
